import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, fileUrl } from "@/lib/api";
import { computePrice, fmtINR } from "@/lib/pricing";

export default function PrintTagPage() {
  const { tag_no } = useParams();
  const [item, setItem] = useState(null);
  const [rates, setRates] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [i, r] = await Promise.all([
          api.get(`/items/${encodeURIComponent(tag_no)}`).then((x) => x.data),
          api.get(`/rates/today`).then((x) => x.data),
        ]);
        setItem(i);
        setRates(r);
        setTimeout(() => window.print(), 500);
      } catch (e) {
        setErr("Item not found");
      }
    })();
  }, [tag_no]);

  if (err) return <div className="p-8 text-center">{err}</div>;
  if (!item) return <div className="p-8 text-center text-ivory/60">Loading…</div>;

  const pExcl = computePrice(item, rates, false);
  const pIncl = computePrice(item, rates, true);
  const catalogueUrl = `${window.location.origin}/catalogue?tag=${encodeURIComponent(item.tag_no)}`;
  const isDiamond = item.diamond_weight > 0;

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-ink-900">
      <div id="print-tag" className="bg-white text-black w-[420px] p-8 border-4 border-[#E3A89C] font-sans shadow-2xl">
        <div className="text-center border-b border-[#E3A89C] pb-3 mb-4">
          <div className="font-display text-2xl tracking-[0.2em]" style={{ fontFamily: 'Fraunces, serif' }}>✦ BHOLAA JEWELS ✦</div>
          <div className="text-[11px] tracking-[0.35em] uppercase mt-1 text-[#8a6b5d]">Est. 1966</div>
        </div>

        <div className="flex gap-4 mb-4">
          {item.image_path && (
            <img src={fileUrl(item.image_path)} alt={item.item_name} className="w-28 h-28 object-cover border border-[#E3A89C]" />
          )}
          <div className="flex-1">
            <div className="font-display text-lg leading-tight" style={{ fontFamily: 'Fraunces, serif' }}>{item.item_name}</div>
            <div className="text-xs mt-1 text-[#555]">Tag No: <b>{item.tag_no}</b></div>
            <div className="text-xs text-[#555]">{item.karat} · {item.category}</div>
          </div>
        </div>

        <table className="w-full text-xs border-t border-b border-[#E3A89C]/50 py-2 my-3">
          <tbody>
            <tr><td className="py-1 text-[#666]">Gold Wt</td><td className="py-1 text-right font-medium">{(item.gold_weight || 0).toFixed(3)} g</td></tr>
            {isDiamond && <tr><td className="py-1 text-[#666]">Diamond Wt</td><td className="py-1 text-right font-medium">{item.diamond_weight.toFixed(3)} ct</td></tr>}
            <tr><td className="py-1 text-[#666]">Gold Rate ({item.karat})</td><td className="py-1 text-right font-medium">{fmtINR(pExcl.karatRate)}/g</td></tr>
          </tbody>
        </table>

        {/* Price block — Feb 2026 spec:
            • Total Price (final)
            • Breakup:
                Gold Value = gold value + making charges merged
                Diamond Value — only when item contains diamonds
            • No percentages anywhere */}
        <div className="mt-3 space-y-2">
          <div className="flex justify-between text-base border-b border-[#E3A89C]/40 pb-2">
            <span className="font-medium">Total Price</span>
            <b className="text-lg">{fmtINR(pExcl.final)}</b>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6b5d] pt-1">Breakup</div>
          <div className="flex justify-between text-xs">
            <span>Gold Value</span>
            <span className="font-medium">{fmtINR((pExcl.goldValue || 0) + (pExcl.makingCharges || 0))}</span>
          </div>
          {isDiamond && (
            <div className="flex justify-between text-xs">
              <span>Diamond Value</span>
              <span className="font-medium">{fmtINR(pExcl.diamondValue)}</span>
            </div>
          )}
          <div className="flex justify-between text-xs border-t border-[#E3A89C]/30 pt-2 mt-1 text-[#666]">
            <span>Price (incl. 3% GST)</span>
            <span className="font-medium">{fmtINR(pIncl.final)}</span>
          </div>
        </div>

        <div className="mt-5 flex justify-between items-end">
          <div className="text-[10px] text-[#888] max-w-[200px] leading-tight">
            Scan to view this piece<br />in our live catalogue.
          </div>
          <QRCodeSVG value={catalogueUrl} size={72} bgColor="#ffffff" fgColor="#000000" level="M" />
        </div>
        <div className="text-[9px] text-center text-[#999] mt-4">Prices auto-calculated from today's rates · Final price may vary</div>
      </div>
      <div className="no-print fixed bottom-6 right-6 flex gap-2">
        <button onClick={() => window.print()} className="px-5 py-3 bg-rose-gold text-ink-900 text-sm font-medium tracking-wide">Print</button>
        <button onClick={() => window.close()} className="px-5 py-3 border border-white/20 text-ivory text-sm">Close</button>
      </div>
    </div>
  );
}
